import React, { useState, useEffect, useCallback } from 'react';
import {
  accessControlApi,
  ACCESS_CONTROL_MODULES,
} from '../../services/api';
import type {
  RoleDto,
  UserRoleAssignmentDto,
  ModulePermissions,
  ModuleKey,
} from '../../services/api';

type TabId = 'roles' | 'assignments';

const MODULE_ICONS: Record<string, React.ReactNode> = {
  dashboard: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  ),
  accounting: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  ),
  sales: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  ),
  purchases: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  inventory: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  ),
  hr: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  projects: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  ),
  reports: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  settings: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
};

const defaultPermissions = (): Record<string, ModulePermissions> => {
  const perms: Record<string, ModulePermissions> = {};
  ACCESS_CONTROL_MODULES.forEach((m) => {
    perms[m.key] = { view: false, create: false, edit: false, delete: false };
  });
  return perms;
};

export default function AccessControlPage() {
  const [activeTab, setActiveTab] = useState<TabId>('roles');
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [assignments, setAssignments] = useState<UserRoleAssignmentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [createRoleOpen, setCreateRoleOpen] = useState(false);
  const [assignUserOpen, setAssignUserOpen] = useState(false);

  const loadRoles = useCallback(() => {
    accessControlApi.listRoles().then((res) => {
      if (res.data) setRoles(res.data);
    });
  }, []);
  const loadAssignments = useCallback(() => {
    accessControlApi.listAssignments().then((res) => {
      if (res.data) setAssignments(res.data);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      accessControlApi.listRoles(),
      accessControlApi.listAssignments(),
    ]).then(([rRes, aRes]) => {
      if (rRes.data) setRoles(rRes.data);
      if (aRes.data) setAssignments(aRes.data);
      setLoading(false);
    });
  }, []);

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <h1 className="text-lg font-bold text-gray-900 mb-1 shrink-0">Access Control</h1>
      <p className="text-sm text-gray-500 mb-4 shrink-0">
        Manage roles, permissions, and user access to modules.
      </p>

      <nav className="flex gap-1 border-b border-gray-200 mb-4 shrink-0" aria-label="Access control tabs">
        <button
          onClick={() => setActiveTab('roles')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
            activeTab === 'roles'
              ? 'border-brand-600 text-brand-700 bg-gray-50'
              : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          Roles & Permissions
        </button>
        <button
          onClick={() => setActiveTab('assignments')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
            activeTab === 'assignments'
              ? 'border-brand-600 text-brand-700 bg-gray-50'
              : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          User Assignments
        </button>
      </nav>

      <div className="bg-white rounded-xl shadow-card border border-gray-100 flex-1 min-h-0 overflow-auto">
        {activeTab === 'roles' && (
          <RolesTab
            loading={loading}
            roles={roles}
            onRefresh={loadRoles}
            onOpenCreate={() => setCreateRoleOpen(true)}
          />
        )}
        {activeTab === 'assignments' && (
          <AssignmentsTab
            loading={loading}
            assignments={assignments}
            roles={roles}
            onRefresh={() => {
              loadAssignments();
              loadRoles();
            }}
            onOpenAssign={() => setAssignUserOpen(true)}
          />
        )}
      </div>

      {createRoleOpen && (
        <CreateRoleModal
          onClose={() => setCreateRoleOpen(false)}
          onSuccess={() => {
            setCreateRoleOpen(false);
            loadRoles();
          }}
        />
      )}
      {assignUserOpen && (
        <AssignUserModal
          roles={roles}
          onClose={() => setAssignUserOpen(false)}
          onSuccess={() => {
            setAssignUserOpen(false);
            loadAssignments();
          }}
        />
      )}
    </div>
  );
}

function RolesTab({
  loading,
  roles,
  onRefresh,
  onOpenCreate,
}: {
  loading: boolean;
  roles: RoleDto[];
  onRefresh: () => void;
  onOpenCreate: () => void;
}) {
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Roles</h2>
          <p className="text-sm text-gray-500">Define roles with specific module permissions</p>
        </div>
        <button
          type="button"
          onClick={onOpenCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Role
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500">Loading…</div>
      ) : roles.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <svg
            className="w-16 h-16 text-gray-300 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <p className="font-medium text-gray-600">No roles defined</p>
          <p className="text-sm text-gray-500 mt-1 mb-6">Create your first role to manage permissions</p>
          <button
            type="button"
            onClick={onOpenCreate}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Role
          </button>
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {roles.map((role) => (
            <li key={role.id} className="py-4 flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">{role.name}</p>
                <p className="text-sm text-gray-500">{role.description || 'No description'}</p>
                <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded ${role.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                  {role.status}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <EditRoleButton role={role} onSaved={onRefresh} />
                <DeleteRoleButton roleId={role.id} roleName={role.name} onDeleted={onRefresh} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AssignmentsTab({
  loading,
  assignments,
  roles,
  onRefresh,
  onOpenAssign,
}: {
  loading: boolean;
  assignments: UserRoleAssignmentDto[];
  roles: RoleDto[];
  onRefresh: () => void;
  onOpenAssign: () => void;
}) {
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">User Assignments</h2>
          <p className="text-sm text-gray-500">Create users and assign roles from Roles & Permissions</p>
        </div>
        <button
          type="button"
          onClick={onOpenAssign}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
          Create User & Assign Role
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500">Loading…</div>
      ) : assignments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          <p className="font-medium text-gray-600">No user assignments</p>
          <p className="text-sm text-gray-500 mt-1 mb-6">Create users and assign roles from Roles & Permissions</p>
          <button
            type="button"
            onClick={onOpenAssign}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700"
          >
            Create User & Assign Role
          </button>
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {assignments.map((a) => (
            <li key={a.id} className="py-4 flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">{a.user_name}</p>
                <p className="text-sm text-gray-500">{a.user_email}</p>
                <p className="text-sm text-gray-600 mt-1">
                  Role: <span className="font-medium">{a.role_name}</span>
                  {a.restricted_modules && a.restricted_modules.length > 0 && (
                    <span className="text-gray-500"> · Restricted to: {a.restricted_modules.join(', ')}</span>
                  )}
                </p>
                <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded ${a.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                  {a.status}
                </span>
              </div>
              <DeleteAssignmentButton assignmentId={a.id} userName={a.user_name} onDeleted={onRefresh} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CreateRoleModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [name, setName] = useState('');
  const [status, setStatus] = useState('Active');
  const [description, setDescription] = useState('');
  const [permissions, setPermissions] = useState<Record<string, ModulePermissions>>(defaultPermissions());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setPerm = (moduleKey: string, field: keyof ModulePermissions, value: boolean) => {
    setPermissions((prev) => ({
      ...prev,
      [moduleKey]: { ...(prev[moduleKey] || {}), [field]: value },
    }));
  };

  const setAllForModule = (moduleKey: string, value: boolean) => {
    setPermissions((prev) => ({
      ...prev,
      [moduleKey]: { view: value, create: value, edit: value, delete: value },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Role name is required.');
      return;
    }
    setSaving(true);
    const res = await accessControlApi.createRole({
      name: name.trim(),
      status,
      description: description.trim() || undefined,
      permissions,
    });
    setSaving(false);
    if (res.data) {
      onSuccess();
    } else {
      setError(res.message || (res.errors && Object.values(res.errors).flat().join(' ')) || 'Failed to create role.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 shrink-0">
            <div className="flex items-center gap-2">
              <svg className="w-6 h-6 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <h2 className="text-lg font-semibold text-gray-900">Create New Role</h2>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Sales Manager"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what this role is for"
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 resize-y"
                />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">Module Permissions</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Module</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">View</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Create</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Edit</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Delete</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">All</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {ACCESS_CONTROL_MODULES.map((m) => {
                        const p = permissions[m.key] || {};
                        const all = !!(p.view && p.create && p.edit && p.delete);
                        return (
                          <tr key={m.key}>
                            <td className="px-3 py-2 flex items-center gap-2">
                              <span className="text-gray-400">{MODULE_ICONS[m.key] ?? null}</span>
                              <span className="text-sm text-gray-900">{m.label}</span>
                            </td>
                            {(['view', 'create', 'edit', 'delete'] as const).map((f) => (
                              <td key={f} className="px-2 py-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={!!p[f]}
                                  onChange={(e) => setPerm(m.key, f, e.target.checked)}
                                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                                />
                              </td>
                            ))}
                            <td className="px-2 py-2 text-center">
                              <button
                                type="button"
                                role="switch"
                                aria-checked={all}
                                onClick={() => setAllForModule(m.key, !all)}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${all ? 'bg-brand-600' : 'bg-gray-200'}`}
                              >
                                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${all ? 'translate-x-5' : 'translate-x-1'}`} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 shrink-0">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50">
                {saving ? 'Creating…' : 'Create Role'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function EditRoleButton({ role, onSaved }: { role: RoleDto; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="text-sm text-brand-600 hover:text-brand-700">
        Edit
      </button>
      {open && (
        <EditRoleModal
          role={role}
          onClose={() => setOpen(false)}
          onSuccess={() => {
            setOpen(false);
            onSaved();
          }}
        />
      )}
    </>
  );
}

function EditRoleModal({ role, onClose, onSuccess }: { role: RoleDto; onClose: () => void; onSuccess: () => void }) {
  const [name, setName] = useState(role.name);
  const [status, setStatus] = useState(role.status);
  const [description, setDescription] = useState(role.description || '');
  const [permissions, setPermissions] = useState<Record<string, ModulePermissions>>(() => {
    const p = defaultPermissions();
    if (role.permissions && typeof role.permissions === 'object') {
      Object.keys(role.permissions).forEach((k) => {
        if (p[k]) p[k] = { ...p[k], ...role.permissions[k] };
      });
    }
    return p;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setPerm = (moduleKey: string, field: keyof ModulePermissions, value: boolean) => {
    setPermissions((prev) => ({ ...prev, [moduleKey]: { ...(prev[moduleKey] || {}), [field]: value } }));
  };
  const setAllForModule = (moduleKey: string, value: boolean) => {
    setPermissions((prev) => ({ ...prev, [moduleKey]: { view: value, create: value, edit: value, delete: value } }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Role name is required.');
      return;
    }
    setSaving(true);
    const res = await accessControlApi.updateRole(role.id, { name: name.trim(), status, description: description.trim() || undefined, permissions });
    setSaving(false);
    if (res.data) onSuccess();
    else setError(res.message || 'Failed to update role.');
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Edit Role</h2>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role Name *</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500">
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 resize-y" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">Module Permissions</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Module</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">View</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Create</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Edit</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Delete</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">All</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {ACCESS_CONTROL_MODULES.map((m) => {
                        const p = permissions[m.key] || {};
                        const all = !!(p.view && p.create && p.edit && p.delete);
                        return (
                          <tr key={m.key}>
                            <td className="px-3 py-2 flex items-center gap-2">
                              <span className="text-gray-400">{MODULE_ICONS[m.key] ?? null}</span>
                              <span className="text-sm text-gray-900">{m.label}</span>
                            </td>
                            {(['view', 'create', 'edit', 'delete'] as const).map((f) => (
                              <td key={f} className="px-2 py-2 text-center">
                                <input type="checkbox" checked={!!p[f]} onChange={(e) => setPerm(m.key, f, e.target.checked)} className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                              </td>
                            ))}
                            <td className="px-2 py-2 text-center">
                              <button type="button" role="switch" aria-checked={all} onClick={() => setAllForModule(m.key, !all)} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${all ? 'bg-brand-600' : 'bg-gray-200'}`}>
                                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${all ? 'translate-x-5' : 'translate-x-1'}`} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function DeleteRoleButton({ roleId, roleName, onDeleted }: { roleId: string; roleName: string; onDeleted: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    setDeleting(true);
    const res = await accessControlApi.deleteRole(roleId);
    setDeleting(false);
    if (res.success) {
      setConfirming(false);
      onDeleted();
    }
  };
  if (!confirming) {
    return (
      <button type="button" onClick={() => setConfirming(true)} className="text-sm text-red-600 hover:text-red-700">
        Delete
      </button>
    );
  }
  return (
    <span className="flex items-center gap-2">
      <span className="text-sm text-gray-600">Delete &quot;{roleName}&quot;?</span>
      <button type="button" onClick={handleDelete} disabled={deleting} className="text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50">Yes</button>
      <button type="button" onClick={() => setConfirming(false)} className="text-sm text-gray-600 hover:text-gray-800">No</button>
    </span>
  );
}

function AssignUserModal({
  roles,
  onClose,
  onSuccess,
}: {
  roles: RoleDto[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [roleId, setRoleId] = useState('');
  const [status, setStatus] = useState('Active');
  const [restrictedModules, setRestrictedModules] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleModule = (key: ModuleKey) => {
    setRestrictedModules((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !email.trim() || !password || !roleId) {
      setError('Please fill in Name, Email, Password, and Assign Role.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== passwordConfirmation) {
      setError('Password and confirmation do not match.');
      return;
    }
    setSaving(true);
    const res = await accessControlApi.createUserAndAssign({
      name: name.trim(),
      email: email.trim(),
      password,
      password_confirmation: passwordConfirmation,
      role_id: roleId,
      status,
      restricted_modules: restrictedModules.length > 0 ? restrictedModules : undefined,
    });
    setSaving(false);
    if (res.data) {
      onSuccess();
    } else {
      setError(res.message || (res.errors && Object.values(res.errors).flat().join(' ')) || 'Failed to create user and assign role.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <svg className="w-6 h-6 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              <h2 className="text-lg font-semibold text-gray-900">Create User & Assign Role</h2>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 8 characters"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
              <input
                type="password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                placeholder="Confirm password"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assign Role *</label>
              <select
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              >
                <option value="">Choose a role</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Restrict to Specific Modules (Optional)</label>
              <p className="text-xs text-gray-500 mb-2">Leave empty to grant access to all modules defined in the role.</p>
              <div className="grid grid-cols-2 gap-2">
                {ACCESS_CONTROL_MODULES.map((m) => (
                  <label key={m.key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={restrictedModules.includes(m.key)}
                      onChange={() => toggleModule(m.key)}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="text-sm text-gray-700">{m.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50">
                {saving ? 'Creating…' : 'Create User & Assign'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function DeleteAssignmentButton({
  assignmentId,
  userName,
  onDeleted,
}: {
  assignmentId: string;
  userName: string;
  onDeleted: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    setDeleting(true);
    const res = await accessControlApi.deleteAssignment(assignmentId);
    setDeleting(false);
    if (res.success) {
      setConfirming(false);
      onDeleted();
    }
  };
  if (!confirming) {
    return (
      <button type="button" onClick={() => setConfirming(true)} className="text-sm text-red-600 hover:text-red-700">
        Remove
      </button>
    );
  }
  return (
    <span className="flex items-center gap-2">
      <span className="text-sm text-gray-600">Remove {userName}?</span>
      <button type="button" onClick={handleDelete} disabled={deleting} className="text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50">Yes</button>
      <button type="button" onClick={() => setConfirming(false)} className="text-sm text-gray-600 hover:text-gray-800">No</button>
    </span>
  );
}
